import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { List, Collapse } from '@mui/material';
// guards
import RoleBasedGuard from '../../../guards/RoleBasedGuard';
// sidebar palette
import { TILE_CENTER } from '../../../layouts/dashboard/navbar/sidebarTokens';
//
import { NavItemRoot, NavItemSub } from './NavItem';
import { getActive } from '..';

// ----------------------------------------------------------------------

// Sub-items hang off a hairline that drops from the centre of the parent's
// icon tile — one drawn line, with the sub-item dots sitting on it.
const subListSx = {
  position: 'relative',
  ml: `${TILE_CENTER}px`,
  pl: '10px',
  mb: 0.5,
  borderLeft: '1px solid var(--sb-line)'
};

NavListRoot.propTypes = {
  list: PropTypes.object,
  isCollapse: PropTypes.bool
};

export function NavListRoot({ list, isCollapse }) {
  const { pathname } = useLocation();

  const active = getActive(list.path, pathname);

  const [open, setOpen] = useState(active);

  const hasChildren = list.children;

  if (hasChildren) {
    // The guard has to wrap the Collapse as well as the item itself: sub-items
    // carry no `roles` of their own, so an unguarded Collapse renders a bare
    // list/create pair for every hidden entry whose path matches the current
    // URL (and `open` is seeded once at mount, so a refresh keeps it stuck).
    return (
      <RoleBasedGuard roles={list.roles}>
        <NavItemRoot
          item={list}
          isCollapse={isCollapse}
          active={active}
          open={open}
          onOpen={() => setOpen(!open)}
        />

        {!isCollapse && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={subListSx}>
              {(list.children || []).map((item) => (
                <NavListSub key={item.title + item.path} list={item} />
              ))}
            </List>
          </Collapse>
        )}
      </RoleBasedGuard>
    );
  }

  return <NavItemRoot item={list} active={active} isCollapse={isCollapse} />;
}

// ----------------------------------------------------------------------

NavListSub.propTypes = {
  list: PropTypes.object
};

function NavListSub({ list }) {
  const { pathname } = useLocation();

  const active = getActive(list.path, pathname);

  const [open, setOpen] = useState(active);

  const hasChildren = list.children;

  if (hasChildren) {
    return (
      <>
        <NavItemSub
          item={list}
          onOpen={() => setOpen(!open)}
          open={open}
          active={active}
        />

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ ...subListSx, ml: '16px' }}>
            {(list.children || []).map((item) => (
              <NavItemSub
                key={item.title + item.path}
                item={item}
                active={getActive(item.path, pathname)}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return <NavItemSub item={list} active={active} />;
}
